"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, IconButton, Select } from "@radix-ui/themes"
import {
  Plus,
  Edit,
  Trash2,
  Download,
  QrCode,
  Users,
  MapPin,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  MoreVertical,
  X
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import TableModal from "../../components/TableModal"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface Table {
  _id: string
  tableNumber: string
  restaurant: string
  capacity: number
  location: "indoor" | "outdoor" | "private"
  status: "available" | "occupied" | "reserved" | "maintenance"
  qrCode: {
    code: string
    url: string
    imageUrl?: string
  }
  currentOrder?: string | { _id: string; orderNumber: string; status: string }
  isActive: boolean
  notes?: string
  createdAt: string
  updatedAt: string
}

interface TableFormData {
  tableNumber: string
  capacity: number
  location: "indoor" | "outdoor" | "private"
  notes: string
}

const RestaurantTablesPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { t, language, dir } = useLanguage()
  const [tables, setTables] = useState<Table[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingTable, setEditingTable] = useState<Table | null>(null)
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [statusFilter, setStatusFilter] = useState("all")
  const [locationFilter, setLocationFilter] = useState("all")

  console.log("RestaurantTablesPage - restaurantId:", restaurantId)

  useEffect(() => {
    fetchTables()
  }, [restaurantId])

  const fetchTables = async () => {
    try {
      setLoading(true)
      const response = await restaurantPortalAPI.getTables(restaurantId!)
      setTables(response.data)
    } catch (error) {
      console.error("Error fetching tables:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleTableSubmit = async (tableId: string | TableFormData, data?: TableFormData) => {
    try {
      if (typeof tableId === 'string' && data) {
        // Update existing table
        // await restaurantPortalAPI.updateTable(tableId, data)
        console.log("Update table:", tableId, data)
        setShowModal(false)
        setEditingTable(null)
      } else {
        // Create new table
        const formData = tableId as TableFormData
        const tableData = {
          ...formData,
          restaurant: restaurantId
        }
        console.log("Creating table with data:", tableData)
        await restaurantPortalAPI.createTable(tableData)
        setShowModal(false)
      }
      fetchTables()
    } catch (error) {
      console.error("Error saving table:", error)
    }
  }

  const handleDeleteTable = async (tableId: string) => {
    if (window.confirm(t("deleteTableConfirm"))) {
      try {
        await restaurantPortalAPI.deleteTable(tableId)
        setTables(tables => (tables || []).filter(table => table._id !== tableId))
      } catch (error) {
        console.error("Error deleting table:", error)
      }
    }
  }

  const handleStatusUpdate = async (tableId: string, newStatus: string) => {
    try {
      console.log("Updating table status:", { tableId, newStatus })
      const response = await restaurantPortalAPI.updateTableStatus(tableId, { status: newStatus })
      console.log("Table status update response:", response.data)

      setTables(tables =>
        (tables || []).map(table =>
          table._id === tableId
            ? { ...table, status: newStatus as any }
            : table
        )
      )
    } catch (error: any) {
      console.error("Error updating table status:", error)
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
    }
  }

  const handleDownloadQR = async (tableId: string) => {
    try {
      const response = await restaurantPortalAPI.downloadQRCode(tableId)
      const blob = new Blob([response.data], { type: 'image/png' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `table-qr-${tableId}.png`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error("Error downloading QR code:", error)
    }
  }

  const getStatusColor = (status: any) => {
    if (typeof status !== 'string') return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    switch (status) {
      case "available":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      case "occupied":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      case "reserved":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
      case "maintenance":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    }
  }

  const getStatusIcon = (status: any) => {
    if (typeof status !== 'string') return <AlertCircle className="w-4 h-4" />
    switch (status) {
      case "available":
        return <CheckCircle className="w-4 h-4" />
      case "occupied":
        return <AlertCircle className="w-4 h-4" />
      case "reserved":
        return <Clock className="w-4 h-4" />
      case "maintenance":
        return <XCircle className="w-4 h-4" />
      default:
        return <MoreVertical className="w-4 h-4" />
    }
  }

  const getLocationIcon = (location: any) => {
    if (typeof location !== 'string') return <MapPin className="w-4 h-4" />
    switch (location) {
      case "indoor":
        return <MapPin className="w-4 h-4" />
      case "outdoor":
        return <MapPin className="w-4 h-4" />
      case "private":
        return <Users className="w-4 h-4" />
      default:
        return <MapPin className="w-4 h-4" />
    }
  }

  const getOrderNumber = (currentOrder: string | { _id: string; orderNumber: string; status: string } | undefined): string => {
    if (!currentOrder) return t("notAvailable")
    if (typeof currentOrder === 'string') return currentOrder
    return currentOrder.orderNumber || t("notAvailable")
  }

  const filteredTables = (tables || []).filter(table => {
    // Safety check: ensure table has required properties
    if (!table || typeof table.status !== 'string' || typeof table.location !== 'string') {
      return false
    }

    const matchesStatus = statusFilter === "all" || table.status === statusFilter
    const matchesLocation = locationFilter === "all" || table.location === locationFilter
    return matchesStatus && matchesLocation
  })

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingTables")} />
  }

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <PageHeader
        title={t("tableManagement")}
        subtitle={t("manageTables")}
        action={
          <Button onClick={() => setShowModal(true)} size="3">
            <Plus size={16} />
            {t("addTable")}
          </Button>
        }
      />

      {/* Stats */}
      <Grid columns={{ initial: "1", md: "4" }} gap="4">
        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{ padding: "16px", borderRadius: "var(--radius-4)", backgroundColor: "var(--blue-4)" }}>
              <Users size={24} style={{ color: "var(--blue-11)" }} />
            </Box>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium" color="gray">{t("totalTables")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: "1" }}>{(tables || []).length}</Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{ padding: "16px", borderRadius: "var(--radius-4)", backgroundColor: "var(--green-4)" }}>
              <CheckCircle size={24} style={{ color: "var(--green-11)" }} />
            </Box>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium" color="gray">{t("available")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: "1" }}>{(tables || []).filter(t => t.status === "available").length}</Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{ padding: "16px", borderRadius: "var(--radius-4)", backgroundColor: "var(--red-4)" }}>
              <AlertCircle size={24} style={{ color: "var(--red-11)" }} />
            </Box>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium" color="gray">{t("occupied")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: "1" }}>{(tables || []).filter(t => t.status === "occupied").length}</Text>
            </Flex>
          </Flex>
        </Card>

        <Card size="2" style={{ padding: "8px" }}>
          <Flex align="center" gap="5">
            <Box style={{ padding: "16px", borderRadius: "var(--radius-4)", backgroundColor: "var(--yellow-4)" }}>
              <Clock size={24} style={{ color: "var(--yellow-11)" }} />
            </Box>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium" color="gray">{t("reserved")}</Text>
              <Text size="7" weight="bold" style={{ lineHeight: "1" }}>{(tables || []).filter(t => t.status === "reserved").length}</Text>
            </Flex>
          </Flex>
        </Card>
      </Grid>

      {/* Filters */}
      <Card>
        <Grid columns={{ initial: "1", md: "3" }} gap="4" align="center">
          <Select.Root value={statusFilter} onValueChange={setStatusFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("allStatus")} />
            <Select.Content>
              <Select.Item value="all">{t("allStatus")}</Select.Item>
              <Select.Item value="available">{t("available")}</Select.Item>
              <Select.Item value="occupied">{t("occupied")}</Select.Item>
              <Select.Item value="reserved">{t("reserved")}</Select.Item>
              <Select.Item value="maintenance">{t("maintenance")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Select.Root value={locationFilter} onValueChange={setLocationFilter}>
            <Select.Trigger style={{ width: "100%" }} placeholder={t("allLocations")} />
            <Select.Content>
              <Select.Item value="all">{t("allLocations")}</Select.Item>
              <Select.Item value="indoor">{t("indoor")}</Select.Item>
              <Select.Item value="outdoor">{t("outdoor")}</Select.Item>
              <Select.Item value="private">{t("private")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Text size="2" color="gray">
            {t("tablesCount", { count: filteredTables.length, total: (tables || []).length })}
          </Text>
        </Grid>
      </Card>

      {/* Tables Grid */}
      <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="4">
        {filteredTables.map((table) => (
          <Card key={table._id}>
            <div className="p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("table")} {table.tableNumber}</h3>
                  <div className="flex items-center mt-1 space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
                      {getStatusIcon(table.status)}
                      <span className="ml-1 capitalize">{typeof table.status === 'string' ? t(table.status) : t("unknown")}</span>
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                      {getLocationIcon(table.location)}
                      <span className="ml-1 capitalize">{typeof table.location === 'string' ? t(table.location) : t("unknown")}</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleDownloadQR(table._id)}
                    className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title={t("downloadQRCode")}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingTable(table)
                      setShowModal(true)
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title={t("editTable")}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTable(table._id)}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title={t("deleteTable")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t("capacity")}:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{table.capacity} {t("people")}</span>
                </div>

                {table.currentOrder && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t("currentOrder")}:</span>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      #{getOrderNumber(table.currentOrder)}
                    </span>
                  </div>
                )}

                {table.notes && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{t("notes")}:</span> {table.notes}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t("qrCode")}:</span>
                  <div className="flex items-center space-x-2">
                    <QrCode className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-500 font-mono">
                      {table.qrCode?.code ? table.qrCode.code.slice(-8) : t("notAvailable")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Status Update */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("quickStatus")}:</span>
                  <select
                    value={table.status}
                    onChange={(e) => handleStatusUpdate(table._id, e.target.value)}
                    className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="available">{t("available")}</option>
                    <option value="occupied">{t("occupied")}</option>
                    <option value="reserved">{t("reserved")}</option>
                    <option value="maintenance">{t("maintenance")}</option>
                  </select>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </Grid>

      {filteredTables.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t("noTablesFound")}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {statusFilter !== "all" || locationFilter !== "all"
              ? t("tryAdjustingFilters")
              : t("getStartedByAddingFirstTable")}
          </p>
          {statusFilter === "all" && locationFilter === "all" && (
            <Button onClick={() => setShowModal(true)} size="3" className="mt-4">
              <Plus size={16} />
              {t("addTable")}
            </Button>
          )}
        </div>
      )}

      {/* Table Modal */}
      <TableModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingTable(null)
        }}
        table={editingTable}
        restaurantId={restaurantId!}
        onSubmit={handleTableSubmit}
      />
    </div>
  )
}

export default RestaurantTablesPage
